/**
 * Colour values a merchant types, and what may safely be painted from them.
 *
 * A colour field is free text on the way in - the server stores whatever was
 * sent. So nothing here assumes a valid colour: it decides which values are
 * paintable, and leaves the rest to be shown as text rather than dropped.
 */

/**
 * A six-digit hex - the one notation the native colour input emits and a
 * swatch can be painted from without guessing at the browser's parsing.
 */
const HEX = /^#[0-9a-f]{6}$/i;

export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX.test(value.trim());
}

/**
 * The colours a colour metafield carries: one for a single field, several for
 * a list. Values that are not hex are kept, so a typo shows up on the page as
 * the text it is instead of vanishing.
 */
export function colorValues(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : [value];
  return raw
    .filter((item): item is string => typeof item === 'string' && item.trim() !== '')
    .map((item) => item.trim());
}

/**
 * A hex colour held anywhere in a metaobject entry's values, so a "Colour"
 * entry renders as a swatch without the caller having to know which of the
 * merchant's fields carries it.
 */
export function swatchOf(values: Record<string, unknown>): string | undefined {
  return Object.values(values).find(isHexColor)?.trim();
}

function channels(hex: string): [number, number, number] {
  const clean = hex.trim().slice(1);
  return [0, 2, 4].map((at) => parseInt(clean.slice(at, at + 2), 16)) as [number, number, number];
}

function toHex(values: readonly number[]): string {
  return `#${values.map((value) => Math.round(value).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * `from` moved `amount` of the way towards `to`, both six-digit hexes - how a
 * quiet text or a border shade is drawn from the two colours an area is given.
 */
export function mixHex(from: string, to: string, amount: number): string {
  const a = channels(from);
  const b = channels(to);
  return toHex(a.map((value, index) => value + (b[index] - value) * amount));
}

/** WCAG relative luminance of a six-digit hex, 0 for black to 1 for white. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = channels(hex).map((value) => {
    const unit = value / 255;
    return unit <= 0.03928 ? unit / 12.92 : ((unit + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hexes: 1 for the same colour, 21 for black on white. */
export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
}

/** The readable text for a background: near-black on a light one, white on a dark one. */
export const READABLE_DARK = '#0b1220';
export const READABLE_LIGHT = '#ffffff';

export function readableTextOn(background: string): string {
  return contrastRatio(background, READABLE_DARK) >= contrastRatio(background, READABLE_LIGHT)
    ? READABLE_DARK
    : READABLE_LIGHT;
}

/** The contrast WCAG AA asks of body text. */
export const MIN_TEXT_CONTRAST = 4.5;
